import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { API_CONFIG, ApiConfig } from '../../api-config';

/**
 * Shape de un item de select para monedas.
 */
export interface CoinSelectOption {
  value: string;
  label: string;
}

/**
 * Service para cargar las opciones de moneda y tipo de moneda
 * para los selects del tab "Monedas" del edit de país.
 *
 * Endpoints:
 *   GET {apiUrl}/selects/coins
 *   → { success: true, data: [{ id, text }] }
 *
 *   GET {apiUrl}/selects/type-coins?countryId={countryId}
 *   → { success: true, data: [{ id, text }] }
 *
 * Uso en el frontend:
 *   - `getCoins()` carga las opciones de moneda (sin countryId).
 *   - `getCoinTypes(countryId)` carga las opciones de tipo de moneda
 *     filtradas por país.
 *
 * Cache:
 *   - `getCoins()` se cachea globalmente (no depende de countryId).
 *   - `getCoinTypes(countryId)` se cachea por countryId.
 */
@Injectable({ providedIn: 'root' })
export class CoinSelectService {
  private readonly httpClient = inject(HttpClient);
  private readonly apiConfig = inject<ApiConfig>(API_CONFIG);

  private readonly coinsCache = new Map<string, CoinSelectOption[]>();
  private readonly coinTypesCache = new Map<string, CoinSelectOption[]>();

  /**
   * Carga las opciones de moneda desde /selects/coins.
   * Cache global: no depende de countryId.
   */
  getCoins(): Observable<CoinSelectOption[]> {
    const cacheKey = 'all';
    const cached = this.coinsCache.get(cacheKey);
    if (cached) {
      return of(cached);
    }

    return this.httpClient
      .get(`${this.apiConfig.apiUrl}/selects/coins`, {
        observe: 'response',
        responseType: 'text',
      })
      .pipe(
        map((resp) => this.parseResponse(resp.body)),
        tap((options) => {
          this.coinsCache.set(cacheKey, options);
        }),
        catchError(() => {
          return of([]);
        }),
      );
  }

  /**
   * Carga las opciones de tipo de moneda desde /selects/type-coins.
   * Cache por countryId.
   */
  getCoinTypes(
    countryId: string | null | undefined,
  ): Observable<CoinSelectOption[]> {
    if (!countryId) {
      return of([]);
    }

    const cached = this.coinTypesCache.get(countryId);
    if (cached) {
      return of(cached);
    }

    return this.httpClient
      .get(
        `${this.apiConfig.apiUrl}/selects/type-coins?countryId=${countryId}`,
        {
          observe: 'response',
          responseType: 'text',
        },
      )
      .pipe(
        map((resp) => this.parseResponse(resp.body)),
        tap((options) => {
          this.coinTypesCache.set(countryId, options);
        }),
        catchError(() => {
          return of([]);
        }),
      );
  }

  /**
   * Parsea el body del response (que viene como `text` por la
   * convención del proyecto de hacer JSON.parse manual) y mapea
   * a `CoinSelectOption[]`.
   *
   * Convención de respuesta: `{ success: true, data: [{ id, text }] }`.
   */
  private parseResponse(body: unknown): CoinSelectOption[] {
    if (typeof body !== 'string' || !body) return [];
    try {
      const parsed = JSON.parse(body) as {
        success?: boolean;
        data?: Array<{ id: string; text?: string }>;
      };
      if (!parsed || parsed.success !== true || !Array.isArray(parsed.data)) {
        return [];
      }
      return parsed.data.map((item) => ({
        value: item.id || '',
        label: item.text || '',
      }));
    } catch {
      return [];
    }
  }
}
